/**
 * CoreManagerAudio - Centralized audio management singleton
 *
 * Handles all music and SFX playback with:
 * - Scene-to-music mapping (replicating C3's mapMusicMapping)
 * - SFX categories with appropriate volume multipliers
 * - Volume persistence via localStorage
 * - Singleton pattern for global access
 */

type SfxCategory = 'ui' | 'projectile' | 'explosion' | 'entity' | 'destruction';

interface MusicTrack {
  key: string;
  baseVolume: number;
}

class CoreManagerAudio {
  private static instance: CoreManagerAudio;
  private game: Phaser.Game | null = null;
  private currentMusic: Phaser.Sound.BaseSound | null = null;
  private currentMusicKey: string = '';
  private musicVolume: number = 1;
  private sfxVolume: number = 1;

  // Scene to music mapping (from C3's mapMusicMapping)
  private readonly musicMapping: Record<string, MusicTrack> = {
    'MainMenu': { key: 'music_intro', baseVolume: 0.5 },
    'LevelSelect': { key: 'music_space_station', baseVolume: 0.5 },
    'WeaponWorkshop': { key: 'music_space_station', baseVolume: 0.5 },
    'Pause': { key: 'pause_theme', baseVolume: 0.45 },
    'Death': { key: 'music_game_over', baseVolume: 0.5 },
    'Level1': { key: 'music_alien', baseVolume: 0.6 },
    'Level2': { key: 'music_middle_age', baseVolume: 0.5 },
    'Level3': { key: 'music_neon_gaming', baseVolume: 0.5 },
    'Level4': { key: 'music_cave_temple', baseVolume: 0.5 },
    'Level5': { key: 'music_stranger_things', baseVolume: 0.5 },
    'Level6': { key: 'music_voyageur_galactic', baseVolume: 0.5 },
    'Level7': { key: 'music_caverna2', baseVolume: 0.6 },
  };

  // SFX category volume multipliers
  private readonly sfxMultipliers: Record<SfxCategory, number> = {
    ui: 0.3,
    projectile: 0.5,
    explosion: 0.7,
    entity: 0.5,
    destruction: 0.6,
  };

  private constructor() {
    this.loadVolumes();
  }

  /**
   * Get singleton instance
   */
  static getInstance(): CoreManagerAudio {
    if (!CoreManagerAudio.instance) {
      CoreManagerAudio.instance = new CoreManagerAudio();
    }
    return CoreManagerAudio.instance;
  }

  /**
   * Initialize with Phaser game instance
   */
  init(game: Phaser.Game): void {
    this.game = game;
  }

  /**
   * Load volumes from localStorage
   */
  private loadVolumes(): void {
    const savedMusic = localStorage.getItem('imbasion_music_volume');
    const savedSfx = localStorage.getItem('imbasion_sfx_volume');
    this.musicVolume = savedMusic !== null ? parseFloat(savedMusic) : 1;
    this.sfxVolume = savedSfx !== null ? parseFloat(savedSfx) : 1;
  }

  /**
   * Save volumes to localStorage
   */
  private saveVolumes(): void {
    localStorage.setItem('imbasion_music_volume', this.musicVolume.toString());
    localStorage.setItem('imbasion_sfx_volume', this.sfxVolume.toString());
  }

  /**
   * Get current scene from game
   */
  private getCurrentScene(): Phaser.Scene | null {
    if (!this.game) return null;
    const scenes = this.game.scene.getScenes(true);
    return scenes.length > 0 ? scenes[0] : null;
  }

  /**
   * Play music for a specific scene
   * @param sceneKey Scene name (e.g., 'MainMenu', 'Level1')
   */
  playMusicForScene(sceneKey: string): void {
    const scene = this.getCurrentScene();
    if (!scene) {
      console.warn('[CoreManagerAudio] No active scene found');
      return;
    }

    // Get music track for scene
    const track = this.musicMapping[sceneKey];
    if (!track) {
      console.warn(`[CoreManagerAudio] No music mapping for scene: ${sceneKey}`);
      return;
    }

    // Don't restart if same music is already playing
    if (this.currentMusicKey === track.key && this.currentMusic?.isPlaying) {
      return;
    }

    // Stop current music
    this.stopMusic();

    // Check if audio exists
    if (!scene.cache.audio.exists(track.key)) {
      console.warn(`[CoreManagerAudio] Music not loaded: ${track.key}`);
      return;
    }

    // Calculate final volume
    const finalVolume = track.baseVolume * this.musicVolume;

    // Start new music
    this.currentMusic = scene.sound.add(track.key, {
      loop: true,
      volume: finalVolume,
    });
    this.currentMusic.play();
    this.currentMusicKey = track.key;
  }

  /**
   * Stop current music
   */
  stopMusic(): void {
    if (this.currentMusic) {
      this.currentMusic.stop();
      this.currentMusic.destroy();
      this.currentMusic = null;
      this.currentMusicKey = '';
    }
  }

  /**
   * Stop all sounds (music and SFX)
   */
  stopAll(): void {
    const scene = this.getCurrentScene();
    if (scene) {
      scene.sound.stopAll();
    }
    this.currentMusic = null;
    this.currentMusicKey = '';
  }

  /**
   * Play a sound effect
   * @param key SFX key (e.g., 'sfx_laser', 'sfx_explosion_big')
   * @param category Category for volume scaling
   * @param scene Optional scene to play on (uses current scene if not provided)
   */
  playSfx(key: string, category: SfxCategory = 'entity', scene?: Phaser.Scene): void {
    const targetScene = scene || this.getCurrentScene();
    if (!targetScene) return;

    if (this.sfxVolume === 0) return;

    if (!targetScene.cache.audio.exists(key)) {
      // Silently fail - many sounds may not be loaded yet
      return;
    }

    const multiplier = this.sfxMultipliers[category] ?? 0.5;
    const finalVolume = multiplier * this.sfxVolume;

    targetScene.sound.play(key, { volume: finalVolume });
  }

  /**
   * Play UI sound (hover/click)
   * @param type 'hover' or 'click'
   * @param scene Optional scene to play on
   */
  playUISound(type: 'hover' | 'click', scene?: Phaser.Scene): void {
    const key = type === 'hover' ? 'sfx_button_hover' : 'sfx_button_click';
    this.playSfx(key, 'ui', scene);
  }

  /**
   * Set music volume (0-1)
   */
  setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    this.saveVolumes();

    // Update current music volume
    if (this.currentMusic && this.currentMusicKey) {
      const track = Object.values(this.musicMapping).find(t => t.key === this.currentMusicKey);
      const baseVolume = track?.baseVolume ?? 0.5;
      const finalVolume = baseVolume * this.musicVolume;
      (this.currentMusic as Phaser.Sound.WebAudioSound).setVolume(finalVolume);
    }
  }

  /**
   * Set SFX volume (0-1)
   */
  setSfxVolume(volume: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
    this.saveVolumes();

    // Notificar a la escena activa para ajustar loops/SFX en vivo
    const scene = this.getCurrentScene();
    if (scene) {
      scene.events.emit('sfxVolumeChanged', this.sfxVolume);
    }
  }

  /**
   * Get current music volume
   */
  getMusicVolume(): number {
    return this.musicVolume;
  }

  /**
   * Get current SFX volume
   */
  getSfxVolume(): number {
    return this.sfxVolume;
  }

  /**
   * Check if a music track is currently playing
   */
  isMusicPlaying(): boolean {
    return this.currentMusic?.isPlaying ?? false;
  }

  /**
   * Get current music key
   */
  getCurrentMusicKey(): string {
    return this.currentMusicKey;
  }
}

// Export singleton instance
export const audioManager = CoreManagerAudio.getInstance();
